export interface Dimension { id: string; label: string; question: string; help: string }
export interface Config {
  protocol_id: string; dimensions: Dimension[]; cooldown_ms: number;
  practice_objects: number; scored_objects_per_session: number;
  /** Number of ordered picks per dimension (3 in v4); the remaining candidates stay unranked. */
  rank_count: number;
  unlock: { min_drags_per_asset: number; min_active_ms_per_asset: number };
  /** True only for sessions opened with an admin invite code (the study owner's own test runs). */
  admin?: boolean;
}
export interface Session { session_id: string; study_id: string; next_unit_index: number; total_units: number; config: Config }
export interface Study { study_id: string; dataset: string; objects: number; scored_objects: number }
export interface Candidate {
  token: string; label: string; scene_url: string; appearance_url?: string | null; images: Record<string, string>;
  /** Anonymous viewing direction in the three.js frame, derived from evaluated alignment. */
  initial_camera_direction?: [number, number, number] | null;
  /** 512px grid images; empty for studies packed before the compressed distribution. */
  thumbnails?: Record<string, string>;
  /** Provenance, present only when `Config.admin` is true; participants never receive these keys. */
  method_id?: string; method_label?: string;
}
/** Display name: the anonymous letter, followed by ` · <method>` only in an admin session. */
export function candidateName(candidate: Candidate, admin: boolean): string {
  return admin && candidate.method_label ? candidate.label + " · " + candidate.method_label : candidate.label;
}
export interface Telemetry {
  active_ms: number; drag_count: number; reset_count: number; fps: number;
  viewed_images: string[]; load_status: string; simulation_fault_count: number;
}
/** What still blocks a candidate from counting as "operated"; null once it is satisfied. */
export interface InteractionGap { token: string; label: string; operations: number; seconds: number; loading: boolean }

/**
 * One definition of "the participant tried this candidate", mirrored by the server in
 * rank_routes.serve-time validation. An operation is a mouse press on the model (including a
 * welded part that cannot move) or a reset; orbiting the camera is not one. At least one press
 * is always required, so a candidate cannot be unlocked by pressing reset alone.
 */
export function operationCount(item: Telemetry): number {
  return item.drag_count + item.reset_count;
}
export function interactionGap(
  candidate: Candidate, item: Telemetry, unlock: Config["unlock"], admin = false,
): InteractionGap | null {
  const operations = Math.max(0, unlock.min_drags_per_asset - operationCount(item)) || (item.drag_count < 1 ? 1 : 0);
  const seconds = Math.max(0, Math.ceil((unlock.min_active_ms_per_asset - item.active_ms) / 1000));
  const loading = item.load_status !== "ready";
  if (!operations && !seconds && !loading) return null;
  return { token: candidate.token, label: candidateName(candidate, admin), operations, seconds, loading };
}

export type Orders = Record<string, string[]>;
export interface Unit {
  unit_token: string; unit_index: number; is_practice: boolean; prompt: string;
  references: Record<string, string>; candidates: Candidate[];
  draft: { rankings?: Orders; telemetry?: Record<string, Telemetry> }; draft_revision: number; submitted: boolean;
}
export interface Submission {
  session_id: string; unit_token: string; request_id: string;
  rankings: Orders; telemetry: Record<string, Telemetry>;
}
export class ApiError extends Error {
  constructor(public code: string, message: string) { super(message); }
}
export async function request<T>(path: string, body?: unknown, method = "POST"): Promise<T> {
  const response = await fetch(path, body === undefined ? {} : {
    method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  const result = await response.json();
  if (!response.ok) throw new ApiError(result.error?.code ?? "request_failed", result.error?.message ?? "请求失败，请重试");
  return result as T;
}
export const emptyTelemetry = (): Telemetry => ({
  active_ms: 0, drag_count: 0, reset_count: 0, fps: 0, viewed_images: [],
  load_status: "pending", simulation_fault_count: 0,
});
